import { Link, useParams } from "react-router-dom";
import Breadcrumb from "../../Pages/Breadcrumb";
import { Mail, PhoneCall, Printer, Undo2 } from "lucide-react";
import { Helmet } from "react-helmet";

import Timer from "../../../assets/icons/Timer.svg";
import Languages from "../../../assets/icons/Languages.svg";
import Credits from "../../../assets/icons/Credits.svg";
import WP from "../../../assets/icons/Whatsapp.svg";
import TrainingCourseImg from "../../../assets/used-images/defaultImg.jpeg";
import ErrImg from "../../../assets/used-icons/error.png";
import TRCourseData from "./TRCourseData";
import React, { useEffect, useMemo, useRef, useState } from "react";
import CourseDetailsLoading from "../../loading/training-course-details";
import { useUserSelection } from "../../../store/UserPayment";
import axios from "axios";
import Toast from "../../ui/Toast";
import { useReactToPrint } from "react-to-print";
import PDFAR from "./pdf-ar";
import PDFEN from "./pdf-en";
import Image from "../../ui/Image";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "../../../store/language-context";
import { useTranslation } from "react-i18next";

const TrainingCourseDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { URL, lang } = useLanguage();
  const { setRowDetails } = useUserSelection();

  const [copied, setCopied] = useState(false);
  const [priceId, setPriceId] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const contentRef = useRef(null);
  const printFunc = useReactToPrint({ contentRef });

  const { data: course, isLoading, error } = useQuery({
    queryKey: ['course-details', id, URL],
    queryFn: async () => {
      try {
        const response = await axios.get(`${URL}/courses/${id}`);
        if (response.status !== 200) throw new Error(t('custom.err'));
        const data = response?.data?.data;
        return data
      } catch (error) {
        throw new Error(t('custom.err'));
      }
    },
    staleTime: 1000 * 60 * 10,  // 10min
    gcTime: 1000 * 60 * 10,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  document.title = `${isLoading ? t('routes.training-courses-details') :
    `${course?.title || t('routes.training-courses-details')}`} | ${t('routes.training-courses')}`
    || t('routes.training-courses-details');

  const sections = useMemo(() => {
    if (!course) return [];
    return [
      {
        title: t('training-courses.duration'),
        content: `${course?.count_hours} ${t('training-courses.hours')}`,
        icon: Timer,
        isFor: course?.count_hours || false
      },
      {
        title: t('training-courses.lang'),
        content:
          <ul className="flex items-center gap-1">
            {
              course?.courselanguage && course?.courselanguage.length > 0 &&
              course?.courselanguage?.map((lang, i) => (
                <React.Fragment key={i}>
                  <li key={i}>
                    {/* {lang?.title.replaceAll('اللغة', '') || 'اللغة العربية'} */}
                    {lang?.title}
                  </li>
                  {course?.courselanguage?.length >= 2 && <span>|</span>}
                </React.Fragment>
              ))
            }
          </ul>,
        icon: Languages,
        isFor: (course?.courselanguage && course?.courselanguage.length > 0) || false
      },
      {
        title: t('training-courses.certificate'),
        content:
          <span>
            {course?.certificate || course?.certificate === false ? t('custom.yes') : t('custom.no')}
          </span>,
        icon: Credits,
      },
    ];
  }, [course, t])

  useEffect(() => {
    setRowDetails(prev => ({ ...prev, courseId: course?.id }))
  }, [course, setRowDetails]);

  useEffect(() => {
    if (course) {

      document.title = `${course?.title || t('routes.training-courses-details')} | الدورات التدريبية`;

      // Update Open Graph meta tags dynamically
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogImage = document.querySelector('meta[property="og:image"]');
      const ogUrl = document.querySelector('meta[property="og:url"]');
      const ogDescription = document.querySelector('meta[property="og:description"]');

      if (ogTitle) ogTitle.setAttribute('content', course?.title || t('routes.training-courses-details'));
      if (ogImage) ogImage.setAttribute('content', course?.image || "https://demo.ivorytraining.com/attachments/courses/default.jpg"); // Fallback image URL
      if (ogUrl) ogUrl.setAttribute('content', window.location.href);
      if (ogDescription) ogDescription.setAttribute('content', course?.description || "تفاصيل البرنامج التدريبي التدريبية");
    }
  }, [course, isLoading, t]);


  useEffect(() => {
    const storedData = localStorage.getItem('price_id');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      setPriceId(parsedData.city_month_data_id);
    }
    const storedPriceId = localStorage.getItem('price_id');
    const storedSelectedPayment = localStorage.getItem('selectedPayment');

    if (storedPriceId) {
      setPriceId(JSON.parse(storedPriceId));
    }

    if (storedSelectedPayment) {
      setSelectedPayment(JSON.parse(storedSelectedPayment));
    }

    const timer = setTimeout(() => {
      localStorage.removeItem('price_id');
      localStorage.removeItem('selectedPayment');
      setPriceId(null);
    }, 300000);


    return () => clearTimeout(timer);
  }, []);

  if (isLoading) return (
    <CourseDetailsLoading />
  );

  if (error) {
    return (
      <div className='h-dvh w-full flex flex-col justify-center items-center'>
        <div className="w-80 max-lg:w-2/4">
          <img src={ErrImg} alt="error-img" />
        </div>
        <p id='err' className="text-xl">
          {error?.message}
        </p>
      </div>
    )
  };

  return (
    <>
      <Breadcrumb pages={[t('routes.training-courses'), `${course?.title || t('routes.training-courses-details')}`]} />
      <div className="container">
        <main className="py-7  grid grid-cols-[70%_27%] gap-8 max-lg:grid-cols-1 max-lg:px-2 relative z-30">
          <section>
            <section className=" w-full max-lg:w-full">
              <div className="flex justify-between items-start max-sm:flex-col mb-2 border rounded-lg">
                <div className="!text-start p-3">
                  <h2 className="text-primary-tw text-2xl my-2 font-semibold !text-start">
                    {course?.title}
                  </h2>
                  <div className="flex items-center gap-2 text-semi-gray text-base">
                    <p>
                      {course?.subject?.category?.title}
                    </p>
                    {course?.subject?.title && course?.subject?.title !== "-" && (
                      <span className="mb-3 bg-gray-300 w-[1px] h-5"> </span>
                    )}
                    <p>
                      {course?.subject?.title && course?.subject?.title !== "-"
                        ? `${course?.subject?.title}`
                        : ""}
                    </p>
                  </div>

                </div>
                <div className="flex gap-2 items-start p-3 max-md:flex-wrap lg:mt-0">
                  <button onClick={printFunc}
                    className="flex items-center gap-2 text-primary-tw border border-primary-tw rounded-lg py-1 px-4" >
                    <span>
                      {t('training-courses.print')}
                    </span>
                    <span className="text-primary-tw">
                      <Printer size={20} />
                    </span>
                  </button>
                  <button
                    className="flex items-center gap-2 bg-primary-tw text-white py-1 px-2 rounded-md border border-primary-tw ltr:flex-row-reverse"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href)
                      setCopied(true);
                      setTimeout(() => {
                        setCopied(false);
                      }, 5000);
                    }}
                  >
                    <span>
                      <Undo2 size={20} color="white" />
                    </span>
                    <span>{t('training-courses.send')}</span>
                  </button>
                </div>
              </div>
              <div className="w-full h-[32rem] max-lg:w-full max-lg:h-[20rem] rounded-lg relative">
                <img
                  src={course?.image || TrainingCourseImg}
                  alt={course?.title}
                  className={"rounded-t-lg w-full h-full"}
                />
              </div>
            </section>
            <TRCourseData course={course} />
          </section>
          <section>
            <section className="sticky top-8">
              <ul className="border-2 border-secondary-tw p-3 rounded-lg">
                {sections?.map((section, secIndex) => (
                  <React.Fragment key={secIndex}>
                    {section.isFor && (
                      <>
                        <li key={secIndex} className="flex items-center gap-3">
                          <span>
                            <img src={section.icon} alt={section.title} />
                          </span>
                          <div className="flex items-center gap-1">
                            <span>{section.title} :</span>
                            <span className="text-secondary-tw">
                              {section.content}
                            </span>
                          </div>
                        </li>
                        {secIndex !== sections.length - 1 && (
                          <div className="my-3 h-[0.5px] w-full bg-secondary-tw"></div>
                        )}

                      </>
                    )}
                  </React.Fragment>
                ))}
              </ul>
              <div className="border-2 border-secondary-tw p-3 rounded-lg flex items-start gap-3">
                <div className="w-36 h-36 rounded-md relative">
                  <Image
                    src={course?.Responsible_course?.image || TrainingCourseImg}
                    alt={`${course?.Responsible_course?.name}`}
                    className={`rounded-md`}
                  />
                </div>
                <div className="text-start space-y-2">
                  {course?.Responsible_course?.name &&
                    <p className=" text-secondary-tw font-semibold rtl:text-right ltr:text-left">
                      {course?.Responsible_course?.name}
                    </p>
                  }

                  {/* email */}
                  {course?.Responsible_course?.name &&
                    <div className="flex items-center gap-1">
                      <span>
                        <Mail
                          size={20}
                          fill="var(--secondary-color)"
                          color="white"
                        />
                      </span>
                      <Link
                        to={`mailto:${course?.Responsible_course?.email}`}
                        className="text-sm my-1 text-[#212529]"
                      >
                        {course?.Responsible_course?.email}
                      </Link>
                    </div>
                  }

                  {/* number */}
                  {course?.Responsible_course?.phone && (
                    <div className="flex items-center gap-1">
                      <span>
                        <PhoneCall
                          size={20}
                          fill="var(--secondary-color)"
                          color=""
                        />
                      </span>
                      <Link
                        to={`tel:${course?.Responsible_course?.phone}`}
                        className="text-sm my-1 text-common-text"
                        dir="ltr"
                      >
                        {course?.Responsible_course?.phone}
                      </Link>
                    </div>
                  )}

                  {/* whatsapp */}
                  {course?.Responsible_course?.phone && (
                    <div className="flex items-center gap-1">
                      <span>
                        <img src={WP} alt="whatsapp" className="size-5" />
                      </span>
                      <Link className="text-sm my-1 text-common-text" dir="ltr"
                        to={`https://wa.me/${course?.Responsible_course?.phone?.replaceAll(' ', '')}`}>
                        {course?.Responsible_course?.phone}
                      </Link>
                      <span dir="ltr">
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </section>
        </main>
      </div>
      {/* COPIED LINK MESSAGE */}
      <Toast state={copied}>
        <p className="text-white ltr:flex-row-reverse m-0">
          {t('training-courses.copy-msg')}
        </p>
      </Toast>
      <div className="hidden">
            <PDFAR
              ref={contentRef}
              course={course}
              priceId={priceId}
              selectedPayment={selectedPayment}
            />
          </div>
      {/* {
        lang === 'ar' ?
          <div className="hidden">
            <PDFAR
              ref={contentRef}
              course={course}
              priceId={priceId}
              selectedPayment={selectedPayment}
            />
          </div>
          :
          <div className="hidden">
            <PDFEN
              ref={contentRef}
              course={course}
              priceId={priceId}
              selectedPayment={selectedPayment}
            />
          </div>
      } */}
    </>
  );
};

export default TrainingCourseDetails;